import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { LoginGate } from '@/components/auth/LoginGate';
import { logout as endSession, probeSession, type Session } from '@/lib/auth';
import { queryClient, setPasswordChangeRequiredHandler, setUnauthorizedHandler } from '@/lib/query';

interface SessionValue {
  session: Session;
  signOut: () => Promise<void>;
  /**
   * Re-reads `/api/me` and replaces the session with what it says. For the two
   * facts about a session that change while it is open: the temporary-password
   * flag going up (an admin reset this person) or coming down (they changed it).
   */
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

/** Inside `<SessionGate>` there is always a session; outside it there is no app. */
export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession used outside <SessionGate>');
  return value;
}

/**
 * The one place that knows the app can be logged out.
 *
 * Boot probes `GET /api/me`; a 401 means no cookie, so the gate is shown instead
 * of the app. Nothing below this component renders without a session, which is
 * what lets every query assume one.
 *
 * A session can also end while the app is open, and that is the case this
 * component exists to handle properly: the cookie expires, the next query comes
 * back 401, and the gate has to reappear. The route is untouched by that, on
 * purpose. The gate replaces this subtree rather than navigating anywhere, so the
 * URL a person was reading is still the URL in the bar, and signing back in
 * renders that same page again.
 */
export function SessionGate({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    probeSession()
      .then((result) => {
        if (cancelled) return;
        setSession(result);
      })
      .catch(() => {
        if (!cancelled) setSession(null);
      })
      .finally(() => {
        if (!cancelled) setChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * A 401 from anywhere in the cache means this session is over.
   *
   * The handler unregisters itself before it does anything else, which is what
   * makes one expiry one toast: the queries that were in flight alongside the
   * one that noticed will all fail in the same tick, and by then there is no
   * handler left for them to call. It is only registered while there is a
   * session to lose, so the boot probe's own 401 cannot reach it.
   */
  useEffect(() => {
    if (!session) return;
    setUnauthorizedHandler(() => {
      setUnauthorizedHandler(null);
      setSession(null);
      toast.error('Signed out: your session expired', {
        description: 'Sign in again and you will land back on this page.',
      });
    });
    return () => setUnauthorizedHandler(null);
  }, [session]);

  /**
   * The hub refusing everything until the password is changed is, like a 401,
   * about the session and not about the query that noticed. Re-probe rather
   * than flip a flag locally: `/api/me` is the one route still answering, and
   * what it says is what the shell should act on.
   */
  const refresh = useCallback(async () => {
    const next = await probeSession();
    if (next) setSession(next);
  }, []);

  useEffect(() => {
    if (!session) return;
    setPasswordChangeRequiredHandler(() => {
      // Once the session already says so there is nothing new to learn, and a
      // refresh per refused poll would be a second request for every first.
      if (!session.mustChangePassword) void refresh();
    });
    return () => setPasswordChangeRequiredHandler(null);
  }, [session, refresh]);

  /**
   * Nothing the last credential fetched belongs to the next one. Clearing on the
   * way in rather than on the way out matters: at this moment no query is
   * mounted, so the cache empties without a mounted view immediately refetching
   * everything it just lost.
   */
  const onAuthenticated = useCallback((next: Session) => {
    queryClient.clear();
    setSession(next);
  }, []);

  const signOut = useCallback(async () => {
    await endSession();
    setSession(null);
  }, []);

  if (!checked) {
    return (
      <div className="text-fg-3 flex min-h-screen items-center justify-center text-sm">
        Checking your session…
      </div>
    );
  }

  if (!session) return <LoginGate onAuthenticated={onAuthenticated} />;

  return (
    <SessionContext.Provider value={{ session, signOut, refresh }}>
      {children}
    </SessionContext.Provider>
  );
}
